import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { getSettings, getPublicBranding, updateCompanySettings, CompanySettingsPayload } from '@/utils/settings'

export interface CompanySettings {
  companyName: string
  fullAddress: string
  city: string
  country: string
  contactPhone: string
  mobile: string
  email: string
  logo: string
  twitter: string
  facebook: string
  instagram: string
}

interface CompanySettingsContextType {
  settings: CompanySettings
  loading: boolean
  error: string | null
  updateSettings: (newSettings: Partial<CompanySettings>) => void
  saveSettings: () => Promise<void>
}

const CompanySettingsContext = createContext<CompanySettingsContextType | undefined>(undefined)

interface CompanySettingsProviderProps {
  children: ReactNode
}

const defaultSettings: CompanySettings = {
  companyName: 'TailorTrack Solutions',
  fullAddress: '123 Fashion Street, Design District',
  city: 'Dubai',
  country: 'UAE',
  contactPhone: '+971-4-123-4567',
  mobile: '+971-50-987-6543',
  email: 'info@tailortrack.com',
  logo: '/logo.png',
  twitter: '@tailortrack',
  facebook: 'tailortrack',
  instagram: 'tailortrack_official'
}

export function CompanySettingsProvider({ children }: CompanySettingsProviderProps) {
  const { isAuthenticated, user } = useAuth()
  const [settings, setSettings] = useState<CompanySettings>(defaultSettings)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadSettings = async () => {
      if (!isAuthenticated) {
        setSettings(defaultSettings)
        setError(null)
        return
      }

      try {
        setLoading(true)
        if (user?.isAdmin || user?.isSuperAdmin) {
          const response = await getSettings()
          setSettings({ ...defaultSettings, ...response.company })
        } else {
          const response = await getPublicBranding()
          setSettings((prev) => ({
            ...prev,
            companyName: response.companyName || defaultSettings.companyName,
            logo: response.logo || ''
          }))
        }
        setError(null)
      } catch (err) {
        console.error('Failed to load company settings:', err)
        setError(err instanceof Error ? err.message : 'Failed to load company settings')
      } finally {
        setLoading(false)
      }
    }

    void loadSettings()
  }, [isAuthenticated, user?.isAdmin, user?.isSuperAdmin])

  const updateSettings = (newSettings: Partial<CompanySettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }))
  }

  const saveSettings = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const updatedSettings = await updateCompanySettings(settings as CompanySettingsPayload)
      setSettings({ ...defaultSettings, ...updatedSettings })
    } catch (err) {
      console.error('Failed to save company settings:', err)
      setError(err instanceof Error ? err.message : 'Failed to save company settings')
      throw err
    } finally {
      setLoading(false)
    }
  }

  const value: CompanySettingsContextType = {
    settings,
    loading,
    error,
    updateSettings,
    saveSettings
  }

  return (
    <CompanySettingsContext.Provider value={value}>
      {children}
    </CompanySettingsContext.Provider>
  )
}

export function useCompanySettings() {
  const context = useContext(CompanySettingsContext)
  if (context === undefined) {
    throw new Error('useCompanySettings must be used within a CompanySettingsProvider')
  }
  return context
} 
